import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Badge, Button, Card, Collapse, PopoverBody, Table } from 'reactstrap';
import {
  FeatureAudienceDependency,
  FeatureAudienceFlag,
  FeatureAudienceSection,
} from './types';

interface Props {
  sections: FeatureAudienceSection[];
  onEdit?: (flag: FeatureAudienceFlag) => void;
}

// the ticket disables children only when the header is exactly "Nobody"
const isNobody = (flag: FeatureAudienceFlag | null): boolean =>
  !!flag && !flag.enabled && !flag.except_target_audience;

export const dependencyNames = (
  dependencies: FeatureAudienceDependency[]
): string => dependencies.map((d) => d.display_name).join(', ');

// "Everyone except" / "Nobody except" on one line, the exception detail underneath
const useAudienceText = () => {
  const { formatMessage } = useIntl();
  return useCallback(
    (
      flag: FeatureAudienceFlag
    ): { sentence: string; detail: string | null } => {
      const detail = flag.except_target_audience_display;
      if (flag.enabled) {
        return {
          sentence: detail
            ? formatMessage({
                id: 'app.views.organization.feature_administration.audience.everyone_except',
                defaultMessage: 'Everyone except',
              })
            : formatMessage({
                id: 'app.views.organization.feature_administration.audience.everyone',
                defaultMessage: 'Everyone',
              }),
          detail,
        };
      }
      return {
        sentence: detail
          ? formatMessage({
              id: 'app.views.organization.feature_administration.audience.nobody_except',
              defaultMessage: 'Nobody except',
            })
          : formatMessage({
              id: 'app.views.organization.feature_administration.audience.nobody',
              defaultMessage: 'Nobody',
            }),
        detail,
      };
    },
    [formatMessage]
  );
};

const useLockedReasonText = () => {
  const { formatMessage } = useIntl();
  return useCallback(
    (flag: FeatureAudienceFlag): string | undefined => {
      switch (flag.locked_reason) {
        case 'derived_from_data':
          return formatMessage({
            id: 'app.views.organization.feature_administration.locked.derived_from_data',
            defaultMessage: 'Determined by your job families data.',
          });
        case 'conflicts_with_custom_surveys':
          return formatMessage({
            id: 'app.views.organization.feature_administration.locked.conflicts_with_custom_surveys',
            defaultMessage:
              'Custom surveys is in use in your organization. Contact customer support to change this.',
          });
        case 'conflicts_with_ai_manager_reviews':
          return formatMessage({
            id: 'app.views.organization.feature_administration.locked.conflicts_with_ai_manager_reviews',
            defaultMessage:
              'AI-generated manager reviews is in use in your organization. Contact customer support to change this.',
          });
        case 'conflicts_with_pulse_checks':
          return formatMessage({
            id: 'app.views.organization.feature_administration.locked.conflicts_with_pulse_checks',
            defaultMessage:
              'Pulse checks is in use in your organization. Set it to Nobody, with no exceptions, first.',
          });
        case 'conflicts_with_ai_recommendations':
          return formatMessage({
            id: 'app.views.organization.feature_administration.locked.conflicts_with_ai_recommendations',
            defaultMessage:
              'AI recommendations is in use in your organization. Set it to Nobody, with no exceptions, first.',
          });
        case 'editing_disabled':
          return formatMessage({
            id: 'app.views.organization.feature_administration.locked.editing_disabled',
            defaultMessage: 'To change this setting, contact customer support.',
          });
        case 'required':
          return formatMessage({
            id: 'app.views.organization.feature_administration.locked.required',
            defaultMessage: 'This feature must stay enabled.',
          });
        default:
          return flag.editable
            ? undefined
            : formatMessage({
                id: 'app.views.organization.feature_administration.locked.default',
                defaultMessage: 'This feature cannot be changed here.',
              });
      }
    },
    [formatMessage]
  );
};

// the outer table and the nested children tables share these widths so cells line up
const ColumnWidths: FC<{ withActions: boolean }> = ({ withActions }) => (
  <colgroup>
    <col style={{ width: '45%' }} />
    <col />
    {withActions && <col style={{ width: '7rem' }} />}
  </colgroup>
);

// fixed-width slot so names line up whether or not a row has a chevron
const ChevronSlot: FC<{
  expanded?: boolean;
  onToggle?: () => void;
  label: string;
}> = ({ expanded, onToggle, label }) => (
  <span className="d-inline-block flex-shrink-0" style={{ width: '1.75rem' }}>
    {onToggle && (
      <Button
        color="link"
        size="sm"
        className="p-0 text-reset"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-expanded={expanded}
        aria-label={label}
      >
        <i
          className={`fe fe-chevron-down feature-audience-chevron${
            expanded ? ' feature-audience-chevron--expanded' : ''
          }`}
        />
      </Button>
    )}
  </span>
);

interface RowProps {
  flag: FeatureAudienceFlag;
  isHeader: boolean;
  disabledByHeader: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  onEdit?: (flag: FeatureAudienceFlag) => void;
}

const FlagRow: FC<RowProps> = ({
  flag,
  isHeader,
  disabledByHeader,
  expanded,
  onToggle,
  onEdit,
}) => {
  const { formatMessage } = useIntl();
  const audienceText = useAudienceText();
  const lockedReasonText = useLockedReasonText();

  const audience = audienceText(flag);
  // popover targets are id selectors, and flag names contain dots
  const domId = `feature-audience-${flag.name.replace(/\./g, '-')}`;
  // a flag whose parent (section header or a depends_on flag) is Nobody does
  // nothing, so it is shown as Nobody and cannot be edited until the parent is on
  const blocked = flag.blocked_by.length > 0;
  const inert = disabledByHeader || blocked;
  const disabled = inert || !flag.editable;
  const title = disabledByHeader
    ? formatMessage({
        id: 'app.views.organization.feature_administration.locked.section_disabled',
        defaultMessage: 'Enable the section above to change this feature.',
      })
    : blocked
      ? formatMessage(
          {
            id: 'app.views.organization.feature_administration.locked.blocked_by',
            defaultMessage:
              'Requires {names}, currently switched off for everyone.',
          },
          { names: dependencyNames(flag.blocked_by) }
        )
      : lockedReasonText(flag);

  return (
    <tr
      data-testid={`feature-audience-row-${flag.name}`}
      className={
        [
          isHeader ? '' : 'feature-audience-child-row',
          isHeader && expanded ? 'feature-audience-row--open' : '',
          flag.superuser_only ? 'feature-audience-row--superuser' : '',
          inert ? 'text-muted' : '',
        ]
          .join(' ')
          .trim() || undefined
      }
      onClick={isHeader && onToggle ? onToggle : undefined}
      style={isHeader && onToggle ? { cursor: 'pointer' } : undefined}
    >
      <td>
        <div className="d-flex align-items-start">
          <ChevronSlot
            expanded={expanded}
            onToggle={isHeader ? onToggle : undefined}
            label={formatMessage({
              id: 'app.views.organization.feature_administration.toggle_section',
              defaultMessage: 'Toggle section',
            })}
          />
          <div>
            <div className={isHeader ? 'h4 mb-0' : undefined}>
              {flag.display_name}
              {flag.depends_on.length > 0 && (
                <>
                  <span
                    id={`${domId}-requires`}
                    data-testid={`feature-audience-requires-${flag.name}`}
                    className="ms-2 text-muted small"
                  >
                    <i className="fe fe-info" />
                  </span>
                  <UncontrolledPopover
                    placement="top"
                    trigger="hover"
                    target={`${domId}-requires`}
                  >
                    <PopoverBody>
                      <FormattedMessage
                        id="app.views.organization.feature_administration.requires"
                        defaultMessage="Requires {names}"
                        values={{ names: dependencyNames(flag.depends_on) }}
                      />
                    </PopoverBody>
                  </UncontrolledPopover>
                </>
              )}
              {flag.superuser_only && (
                <Badge color="light" className="ms-2 text-muted">
                  <FormattedMessage
                    id="app.views.organization.feature_administration.superuser_only"
                    defaultMessage="Superuser only"
                  />
                </Badge>
              )}
              {flag.has_legacy_override && (
                <>
                  <Badge
                    id={`${domId}-legacy`}
                    color="light"
                    className="ms-2 text-muted"
                  >
                    <FormattedMessage
                      id="app.views.organization.feature_administration.legacy_rule"
                      defaultMessage="Legacy rule"
                    />
                  </Badge>
                  <UncontrolledPopover
                    placement="top"
                    trigger="hover"
                    target={`${domId}-legacy`}
                  >
                    <PopoverBody>
                      <FormattedMessage
                        id="app.views.organization.feature_administration.legacy_rule.tooltip"
                        defaultMessage="
                          A custom rule set up by customer support also applies
                          to this feature and is not shown here. Contact
                          customer support to review it.
                        "
                      />
                    </PopoverBody>
                  </UncontrolledPopover>
                </>
              )}
            </div>
            <div className="small text-muted">{flag.description}</div>
          </div>
        </div>
      </td>
      <td data-testid={`feature-audience-audience-${flag.name}`}>
        {inert ? (
          <div>
            {formatMessage({
              id: 'app.views.organization.feature_administration.audience.nobody',
              defaultMessage: 'Nobody',
            })}
          </div>
        ) : (
          <>
            <div>{audience.sentence}</div>
            {audience.detail && <div>{audience.detail}</div>}
          </>
        )}
      </td>
      {onEdit && (
        <td className="text-end text-nowrap">
          {/* icon only; the accessible name and the hover popover both say Edit */}
          <span id={`${domId}-edit`} className="d-inline-block">
            <Button
              color="light"
              size="sm"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(flag);
              }}
              aria-label={formatMessage(
                {
                  id: 'app.views.organization.feature_administration.edit.title',
                  defaultMessage: 'Edit {name}',
                },
                { name: flag.display_name }
              )}
            >
              <i className="fe fe-edit" />
            </Button>
          </span>
          <UncontrolledPopover
            placement="left"
            trigger="hover"
            target={`${domId}-edit`}
          >
            <PopoverBody>
              {disabled && title ? (
                title
              ) : (
                <FormattedMessage
                  id="app.views.organization.feature_administration.action.edit"
                  defaultMessage="Edit"
                />
              )}
            </PopoverBody>
          </UncontrolledPopover>
        </td>
      )}
    </tr>
  );
};

const FeatureAudienceTable: FC<Props> = ({ sections, onEdit }) => {
  // sections start collapsed
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set()
  );

  const toggle = useCallback((section: string) => {
    setExpandedSections((current) => {
      const next = new Set(current);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  }, []);

  const rows = useMemo(
    () =>
      sections.map((section) => {
        const hasChildren = section.flags.length > 0;
        const expanded = expandedSections.has(section.section);
        const disabledByHeader = isNobody(section.header);
        return { section, hasChildren, expanded, disabledByHeader };
      }),
    [sections, expandedSections]
  );

  const columnCount = onEdit ? 3 : 2;

  return (
    <Card>
      <Table
        className="card-table table-sm mb-0 feature-audience-table"
        style={{ tableLayout: 'fixed' }}
        hover
      >
        <ColumnWidths withActions={!!onEdit} />
        <thead>
          <tr>
            <th>
              <FormattedMessage
                id="app.views.organization.feature_administration.column.name"
                defaultMessage="Name"
              />
            </th>
            <th>
              <FormattedMessage
                id="app.views.organization.feature_administration.column.audience"
                defaultMessage="Audience"
              />
            </th>
            {onEdit && (
              <th className="text-end text-nowrap">
                <FormattedMessage
                  id="app.views.organization.feature_administration.column.action"
                  defaultMessage="Action"
                />
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ section, hasChildren, expanded, disabledByHeader }) => (
            <React.Fragment key={section.section}>
              {section.header && (
                <FlagRow
                  flag={section.header}
                  isHeader
                  disabledByHeader={false}
                  expanded={expanded}
                  onToggle={
                    hasChildren ? () => toggle(section.section) : undefined
                  }
                  onEdit={onEdit}
                />
              )}
              {hasChildren && (
                // one spanning row holds all children so they open like the sidebar:
                // the container grows and reveals full-size rows top to bottom, and
                // like the sidebar the closed Collapse (display: none) is what hides them
                <tr className="feature-audience-children">
                  <td colSpan={columnCount} className="p-0">
                    <Collapse isOpen={expanded}>
                      <table
                        className="table table-sm mb-0 w-100"
                        style={{ tableLayout: 'fixed' }}
                      >
                        <ColumnWidths withActions={!!onEdit} />
                        <tbody>
                          {section.flags.map((flag) => (
                            <FlagRow
                              key={flag.name}
                              flag={flag}
                              isHeader={false}
                              disabledByHeader={disabledByHeader}
                              onEdit={onEdit}
                            />
                          ))}
                        </tbody>
                      </table>
                    </Collapse>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </Card>
  );
};

export default React.memo(FeatureAudienceTable);
